<script setup>
import { remove } from 'lodash-es';
import { storeToRefs } from 'pinia';
import { useModal } from 'vue-final-modal';
import { useAuthStore } from '~/auth/stores/auth.store';
import PmEditView from '~/project-management/components/pm-edit-view.vue';
import { useLayouts } from '~/project-management/composables/pm-layouts.composable';
import { useProjectManagementStore } from '~/project-management/store/pm.store';

const $t = inject('$t');

const auth_store = useAuthStore();
const project_management_store = useProjectManagementStore();

const { update_view, set_active_view } = project_management_store;
const { active_schedule, is_pm_loading, pm_loading_message, tippy_target } = storeToRefs(project_management_store);

const { setLayout } = useLayouts();

const new_view_modal = useModal({
  component: PmEditView,
  attrs: {
    onClose() {
      new_view_modal.close();
    },
    onSave() {
      new_view_modal.close();
    },
  },
});

const state = reactive({
  search_text: '',
  hovering_over_uid: '',
  currently_pinning_view_uid: '',
  view_value: {
    view: null,
  },
});

const views = computed(() => project_management_store?.views);
const active_view = computed(() => project_management_store?.active_view);
const views_menu_items = computed(() => {
  const lower_case_search_text = state.search_text.toLowerCase();
  const all_views = views.value
    .filter(view => view.data?.label?.toLowerCase().includes(lower_case_search_text))
    .map(view => ({ ...view, value: view.uid, label: view.data?.label ?? view.uid }));

  const default_view = remove(all_views, (view) => {
    return view.uid === '__default';
  });
  const pinned_for_me_views = remove(all_views, (view) => {
    return view.data.pin_state === auth_store?.logged_in_user_details?.user_id;
  });
  const pinned_for_everyone_views = remove(all_views, (view) => {
    return view.data.pin_state === 'EVERYBODY';
  });

  return [
    ...default_view,
    ...pinned_for_me_views,
    ...pinned_for_everyone_views,
    ...all_views,
  ];
});
const is_an_exact_match_found = computed(() => {
  const lower_case_search_text = state.search_text.toLowerCase();
  return views.value.some(view => view.data.label?.toLowerCase() === lower_case_search_text);
});
const is_create_option_visible = computed(() => {
  return !is_an_exact_match_found.value && state.search_text.length && active_schedule.value?.actions?.modify_views;
});

function selectView(view_uid) {
  pm_loading_message.value = `${$t('Switching view')}...`;
  is_pm_loading.value = true;
  setTimeout(() => {
    set_active_view(view_uid);
    state.search_text = '';
    is_pm_loading.value = false;
    pm_loading_message.value = '';
  }, 100);
}

function editView(mode, label, view_data = {}) {
  new_view_modal.patchOptions({ attrs: { mode, label, viewData: view_data } });
  new_view_modal.open();
}

async function pinView(view) {
  const target_view_uid = view.uid;
  const target_view = views.value.find(view => view.uid === target_view_uid);

  if (!target_view)
    return;

  if (!target_view.data.pin_state || target_view.data.pin_state === 'NOBODY') {
    const previously_pinned_view = views.value.find(view => view.data.pin_state === auth_store?.logged_in_user_details?.user_id);

    if (previously_pinned_view) {
      previously_pinned_view.data.pin_state = 'NOBODY';
      state.currently_pinning_view_uid = previously_pinned_view.uid;
      await update_view(previously_pinned_view);
      state.currently_pinning_view_uid = null;
    }

    target_view.data.pin_state = auth_store?.logged_in_user_details?.user_id;
  }
  else if (target_view.data.pin_state === auth_store?.logged_in_user_details?.user_id) {
    const previously_pinned_view = views.value.find(view => view.data.pin_state === 'EVERYBODY');

    if (previously_pinned_view) {
      previously_pinned_view.data.pin_state = 'NOBODY';
      state.currently_pinning_view_uid = previously_pinned_view.uid;
      await update_view(previously_pinned_view);
      state.currently_pinning_view_uid = null;
    }

    target_view.data.pin_state = 'EVERYBODY';
  }
  else {
    target_view.data.pin_state = 'NOBODY';
  }

  state.currently_pinning_view_uid = target_view_uid;
  await update_view(target_view);
  state.currently_pinning_view_uid = null;
}

watch(() => active_view.value?.uid, () => {
  setLayout();
});
</script>

<template>
  <Vueform v-model="state.view_value" size="sm">
    <SelectElement
      :key="active_view.uid"
      :default="active_view.uid"
      :items="views_menu_items"
      :search="true"
      :native="false"
      :add-class="{
        select: {
          options: '!p-0 overflow-y-scroll',
          option: '!p-0 !bg-white',
          container: '!border-transparent min-w-32',
          dropdown: '!-bottom-1 min-w-60',
          wrapper: 'justify-between',
          search: '!text-xs font-medium',
        },
      }"
      :remove-class="{
        select: {
          dropdown: 'overflow-y-scroll -left-px',
          wrapper: 'justify-end',
        },
      }"
      :override-class="{
        select: {
          container_focused: 'ring-0',
          containerActive: 'vueform-input-bottom-underline-focused',
        },
      }"
      :can-clear="false"
      :can-deselect="false"
      :close-on-select="false"
      name="view"
      class="w-fit"
      input-type="search"
      autocomplete="off"
      @search-change="state.search_text = $event"
      @change="selectView($event)"
    >
      <template #before-list>
        <div
          class="hover:bg-gray-50 flex items-center cursor-pointer text-sm rounded-lg font-semibold text-gray-700 m-1 px-1 py-2 break-words"
          @click="editView('add', state.search_text)"
        >
          <IconHawkPlus class="w-4 h-4 mr-1 text-gray-600" />
          {{ $t("Create view") }} {{ is_create_option_visible ? `"${state.search_text}"` : '' }}
        </div>
        <div class="border-b border-solid border-gray-200" />
      </template>
      <template #single-label>
        <div class="flex items-center px-3">
          <span class="text-xs font-regular text-gray-600">
            {{ $t("View") }}:&nbsp;
          </span>
          <span class="flex max-w-[157px] items-center truncate text-xs font-medium text-gray-900">
            <template v-if="active_view.uid === '__default'">
              {{ $t('Default') }}
            </template>
            <template v-else>
              {{ active_view.data.label }}
            </template>
          </span>
        </div>
      </template>
      <template #option="{ option }">
        <div
          class="flex justify-between items-center w-full py-3 h-[40px] text-gray-700 hover:text-gray-700"
          @mouseenter="state.hovering_over_uid = option.uid !== '__default' ? option.uid : ''"
          @mouseleave="state.hovering_over_uid = ''"
        >
          <div class="flex items-center">
            <span v-if="option.uid === '__default'" class="text-sm font-medium">
              {{ $t('Default') }}
            </span>
            <span
              v-else
              v-tippy="{
                content: option.data.label,
                appendTo: tippy_target,
              }"
              class="text-sm font-medium truncate"
            >
              {{ option.data.label }}
            </span>
            <IconHawkLoadingCircle v-if="state.currently_pinning_view_uid === option.uid" class="animate-spin h-4 w-4 ml-2" />
            <div
              v-else-if="(option.uid === state.hovering_over_uid) || (option.data.pin_state && option.data.pin_state !== 'NOBODY')"
              v-tippy="{
                content: (option.data.pin_state === 'EVERYBODY' && $t('Pinned for everyone')) || (option.data.pin_state === auth_store?.logged_in_user_details?.user_id && $t('Pinned for you')) || $t('Not pinned'),
                placement: 'bottom',
              }"
              class="flex items-center rounded-full hover:bg-gray-200 p-1.5 ml-2"
              @click="pinView(option)"
            >
              <IconHawkPinnedOne
                v-if="option.data.pin_state === 'EVERYBODY'"
                class="text-primary w-4 h-4 outline-none"
              />
              <IconHawkPin
                v-else
                class="text-primary w-4 h-4 outline-none"
                :class="{
                  'text-yellow-500': option.data.pin_state === auth_store?.logged_in_user_details?.user_id,
                }"
              />
            </div>
          </div>
          <span class="flex items-center">
            <div
              v-if="option.uid === state.hovering_over_uid && active_schedule?.actions?.modify_views"
              class="flex items-center ml-auto rounded-full hover:bg-gray-200 p-1.5"
              @click.stop="editView('edit', option.data.label, option)"
            >
              <IconHawkEdit class="text-primary w-4 h-4" />
            </div>
            <IconHawkCheck v-if="active_view.uid === option.uid" class="text-primary-500 cursor-pointer ml-1 w-5 h-5" />
          </span>
        </div>
      </template>
      <template #no-options>
        <div class="text-sm font-regular px-2 pb-2 m-1">
          {{ $t("No matches") }}
        </div>
      </template>
    </SelectElement>
  </Vueform>
</template>

<style scoped>
:deep() {
  input[value]:not([value=""]) ~ .mask-form-caret {
    display: none;
  }
}
</style>
